import { useState } from "react";
import { motion } from "framer-motion";
import { Head } from "@inertiajs/react";
import { Link, router, usePage } from "@inertiajs/react";
import {
  MapPin, Phone, Award, ArrowLeft, Languages, Calendar, Tag, Briefcase,
  Building, AccessibilityIcon, Dumbbell, Mail, Globe,
  Clock, Users, Car, Home, UserCheck, Timer, CreditCard,
  MessageSquare, ExternalLink, Star, Eye, UserPlus, AlertCircle, CheckCircle2,
  GraduationCap, Stethoscope, Activity
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";

const FRANCE_CENTER: [number, number] = [46.603354, 1.888334];

interface Physio {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  lat: number;
  lng: number;
  specialties: string[];
  isCertified: boolean;
  phone: string;
  profileType: "basic" | "filled" | "certified";
  body_zones?: string[];
  accessibility?: string[];
  languages?: string[];
  equipment?: string[];
  payment_type?: string;
  appointment_mode?: string;
  booking_link?: string;
  practice_locations?: string[];
  home_visit_areas?: string;
  professional_address?: string;
  practice_name?: string;
  practitioners_count?: number;
  parking_type?: string;
  parking_types?: string[];
  professional_email?: string;
  professional_website?: string;
  preferred_contact?: string;
  preferred_contact_details?: string;
  appointment_methods?: string[];
  online_booking_link?: string;
  secretariat_hours?: string;
  new_patients_policy?: string;
  session_duration?: string;
  patient_types?: string[];
  waiting_list?: boolean;
  waiting_list_delay?: string;
  practice_mode?: string;
  practice_modes?: string[];
  honorary_details?: string;
  unconventional_practice_description?: string;
  payment_methods?: string[];
  coordinated_practice?: string[];
  coordinated_practice_details?: string;
  techniques?: string[];
  other_techniques?: string;
  training_courses?: string;
  sports?: string[];
  exercise_types?: string[];
  secondary_address?: string;
  cv_url?: string;
  diploma_year?: number;
  diploma_place?: string;
  diploma_extra_1?: string;
  diploma_extra_2?: string;
  other_activities?: string[];
  other_activities_details?: string;
  association_name?: string;
  rpps_number?: string;
  adeli_number?: string;
  publications?: string;
  other_equipment?: string;
  other_languages?: string;
  other_sports?: string;
  visible?: boolean;
  approved_specialties?: string[];
  page_views_count?: number;
  unique_visitors_count?: number;
  source?: "platform" | "rpps";
  rpps_imported?: boolean;
  claimed?: boolean;
}

interface ExistingClaim {
  id: number;
  status: "pending" | "approved" | "rejected";
  created_at: string;
}

interface PhysiotherapistDetailsProps {
  physio: Physio;
  existing_claim?: ExistingClaim | null;
  can_claim?: boolean;
}

const markerIcon = new Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const formatBodyZone = (zone: string) => {
  const m: Record<string, string> = { "head": "Tête", "neck": "Cou", "torso": "Torse", "pelvis": "Bassin", "left-arm": "Bras", "right-arm": "Bras", "left-hand": "Mains", "right-hand": "Mains", "left-leg": "Jambes", "right-leg": "Jambes", "left-foot": "Pieds", "right-foot": "Pieds" };
  return m[zone] || zone;
};

const formatAccessibility = (access: string) => {
  const m: Record<string, string> = { "wheelchair": "Accès fauteuil roulant", "parking": "Parking", "elevator": "Ascenseur", "wideAccess": "Accès large", "rez-de-chaussée": "Rez-de-chaussée" };
  return m[access] || access;
};

const formatLanguage = (lang: string) => {
  const m: Record<string, string> = { "fr": "Français", "en": "Anglais", "es": "Espagnol", "de": "Allemand", "it": "Italien", "ar": "Arabe" };
  return m[lang] || lang;
};

// Reusable section card
const Section = ({ icon, title, children, className = "" }: { icon: React.ReactNode; title: string; children: React.ReactNode; className?: string }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-6 ${className}`}>
    <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-900 mb-4">
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#008788]/10 text-[#008788]">{icon}</span>
      {title}
    </h2>
    {children}
  </div>
);

// Reusable info row
const InfoRow = ({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-gray-400 mt-0.5">{icon}</span>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="mt-0.5 text-sm text-gray-700">{children}</div>
    </div>
  </div>
);

// Badge list
const BadgeList = ({ items, color = "gray" }: { items: string[]; color?: string }) => {
  const colors: Record<string, string> = {
    gray: "bg-gray-100 text-gray-700",
    teal: "bg-[#008788]/10 text-[#008788]",
    green: "bg-green-50 text-green-700",
    blue: "bg-blue-50 text-blue-700",
    purple: "bg-purple-50 text-purple-700",
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span key={i} className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${colors[color] || colors.gray}`}>{item}</span>
      ))}
    </div>
  );
};

export default function PhysiotherapistDetails({ physio, existing_claim, can_claim }: PhysiotherapistDetailsProps) {
  const { auth } = usePage<{ auth: { user: { id: number; email_address: string; role: string } | null } }>().props;
  const [justification, setJustification] = useState("");
  const [claimSubmitted, setClaimSubmitted] = useState(false);

  const isRppsOnly = physio.source === "rpps";
  const isLoggedIn = !!auth?.user;
  const canClaim = isLoggedIn ? can_claim === true : true;

  const handleClaimSubmit = () => {
    router.post("/claim_requests", { physiotherapist_id: physio.id, justification }, {
      onSuccess: () => setClaimSubmitted(true),
    });
  };

  const {
    firstName, lastName, address, city, lat, lng, specialties, approved_specialties = [],
    isCertified, phone, body_zones = [], accessibility = [], languages = [], equipment = [],
    payment_type, appointment_mode, booking_link, page_views_count = 0, unique_visitors_count = 0,
    practice_locations = [], home_visit_areas, practice_name,
    parking_type, parking_types = [], professional_email, professional_website,
    preferred_contact, preferred_contact_details, appointment_methods = [],
    online_booking_link, secretariat_hours, new_patients_policy, session_duration,
    patient_types = [], waiting_list, waiting_list_delay, practice_mode, practice_modes = [],
    honorary_details, unconventional_practice_description, payment_methods = [],
    coordinated_practice = [], coordinated_practice_details, techniques = [],
    other_techniques, training_courses, sports = [], exercise_types = [],
    secondary_address, cv_url, diploma_year, diploma_place, diploma_extra_1, diploma_extra_2,
    other_activities = [], other_activities_details, association_name,
    rpps_number, adeli_number, publications, other_equipment, other_languages, other_sports,
    practitioners_count
  } = physio;

  const hasValidCoordinates = lat != null && lng != null;
  const mapCenter = hasValidCoordinates ? [lat, lng] as [number, number] : FRANCE_CENTER;
  const uniqueBodyZones = [...new Set(body_zones.map(formatBodyZone))];

  const hasExpertise = specialties?.length > 0 || techniques.length > 0 || uniqueBodyZones.length > 0 || equipment.length > 0 || sports.length > 0 || exercise_types.length > 0 || training_courses || cv_url || diploma_year || diploma_place || diploma_extra_1 || diploma_extra_2;
  const hasCabinet = practice_locations.length > 0 || practitioners_count || (parking_type || parking_types.length > 0) || accessibility.length > 0 || (coordinated_practice.length > 0) || (other_activities.length > 0 || other_activities_details) || association_name || languages.length > 0 || other_languages;
  const hasPractical = new_patients_policy || session_duration || waiting_list !== undefined || (practice_mode || practice_modes.length > 0) || (payment_methods.length > 0 || payment_type) || patient_types.length > 0;
  const hasContact = phone || professional_email || professional_website || preferred_contact || (appointment_methods.length > 0 || appointment_mode) || secretariat_hours || online_booking_link || booking_link;

  return (
    <>
      <Head title={`${firstName} ${lastName} - Kinésithérapeute`} />
      <div className="flex flex-col min-h-screen bg-gray-50">
        <main className="flex-1">

          {/* ── Hero ── */}
          <div className="relative bg-gradient-to-br from-[#015963] via-[#008788] to-[#31953a]">
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
            </div>

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Back */}
              <div className="pt-4 pb-2">
                <button type="button" onClick={() => window.history.back()} className="inline-flex items-center text-sm text-white/70 hover:text-white transition-colors">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Retour aux résultats
                </button>
              </div>

              <div className="pb-8 md:flex md:items-end md:justify-between md:gap-8">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-3xl sm:text-4xl font-bold text-white">{firstName} {lastName}</h1>
                    {isCertified && (
                      <Badge className="bg-amber-400 text-amber-950 hover:bg-amber-300 shadow-sm">
                        <Star className="h-3.5 w-3.5 mr-1 fill-current" />
                        Approuvé Ethos
                      </Badge>
                    )}
                  </div>

                  {practice_name && (
                    <p className="mt-1.5 text-lg text-white/75">{practice_name}</p>
                  )}

                  <div className="mt-2 flex items-center text-white/65 text-sm">
                    <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />
                    {address}{secondary_address ? `, ${secondary_address}` : ''}, {city}
                  </div>

                  {/* Diplômes */}
                  {(diploma_year || diploma_place || diploma_extra_1 || diploma_extra_2) && (
                    <div className="mt-2 text-sm text-white/60 flex items-center gap-1.5">
                      <GraduationCap className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        {diploma_year && `${diploma_year}`}{diploma_place && ` — ${diploma_place}`}
                        {diploma_extra_1 && ` · ${diploma_extra_1}`}
                        {diploma_extra_2 && ` · ${diploma_extra_2}`}
                      </span>
                    </div>
                  )}

                  {/* Specialties */}
                  {specialties && specialties.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {specialties.map((specialty, index) => {
                        const isApproved = approved_specialties.includes(specialty);
                        return (
                          <span
                            key={index}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
                              isApproved
                                ? 'bg-amber-400/20 text-amber-200 ring-1 ring-amber-400/30'
                                : 'bg-white/15 text-white/90 ring-1 ring-white/20'
                            }`}
                          >
                            {isApproved && <Star className="h-3 w-3 fill-amber-300 text-amber-300" />}
                            {specialty}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="mt-6 md:mt-0 flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  {phone && (
                    <Button onClick={() => window.open(`tel:${phone}`, "_self")} size="lg" className="bg-white text-[#015963] hover:bg-gray-100 shadow-lg">
                      <Phone className="h-4 w-4 mr-2" />
                      Appeler
                    </Button>
                  )}
                  {professional_email && (
                    <Button onClick={() => window.open(`mailto:${professional_email}`, "_self")} size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── RPPS Banner ── */}
          {isRppsOnly && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="bg-amber-50 border-b border-amber-200">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-900">Ce praticien n'est pas encore inscrit sur Ethos Santé</h3>
                    <p className="text-sm text-amber-700 mt-1">Les informations affichées proviennent de l'annuaire RPPS (données publiques). Ce praticien n'a pas encore complété son profil.</p>

                    {existing_claim ? (
                      <div className="mt-3 p-3 rounded-lg bg-white border border-amber-200">
                        {existing_claim.status === "pending" && <div className="flex items-center gap-2 text-sm text-amber-700"><Clock className="h-4 w-4" /><span>Votre demande de revendication est en cours d'examen.</span></div>}
                        {existing_claim.status === "approved" && <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" /><span>Votre revendication a été approuvée.</span></div>}
                        {existing_claim.status === "rejected" && <div className="flex items-center gap-2 text-sm text-red-700"><AlertCircle className="h-4 w-4" /><span>Votre demande de revendication a été rejetée.</span></div>}
                      </div>
                    ) : claimSubmitted ? (
                      <div className="mt-3 p-3 rounded-lg bg-white border border-green-200">
                        <div className="flex items-center gap-2 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" /><span>Votre demande a été envoyée. Un administrateur l'examinera prochainement.</span></div>
                      </div>
                    ) : isLoggedIn && !canClaim ? (
                      <div className="mt-3 p-3 rounded-lg bg-white border border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600"><AlertCircle className="h-4 w-4" /><span>Vous avez déjà un profil kinésithérapeute actif.</span></div>
                      </div>
                    ) : isLoggedIn ? (
                      <div className="mt-3 space-y-3">
                        <p className="text-sm font-medium text-amber-800">C'est votre fiche ? Revendiquez-la pour compléter votre profil.</p>
                        <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Expliquez brièvement pourquoi cette fiche vous correspond (optionnel)" rows={2} className="bg-white" />
                        <Button onClick={handleClaimSubmit} className="bg-amber-600 hover:bg-amber-700 text-white"><UserPlus className="h-4 w-4 mr-2" />C'est ma fiche</Button>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <Link href={`/registration/new?claim_physiotherapist_id=${physio.id}`}>
                          <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100"><UserPlus className="h-4 w-4 mr-2" />Inscrivez-vous pour revendiquer cette fiche</Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Analytics (owner/admin only) ── */}
          {(page_views_count > 0 || unique_visitors_count > 0) && (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
              <div className="flex items-center gap-6 text-sm text-gray-600 bg-blue-50 p-3 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-blue-600" /><span className="font-semibold">{page_views_count}</span> vues</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /><span className="font-semibold">{unique_visitors_count}</span> visiteurs uniques</div>
              </div>
            </div>
          )}

          {/* ── Content Grid ── */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* ── LEFT COLUMN (2/3) ── */}
              <div className="lg:col-span-2 space-y-6">

                {/* Map */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="relative z-0 rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-72 isolate">
                  <MapContainer center={mapCenter} zoom={hasValidCoordinates ? 15 : 6} style={{ height: "100%", width: "100%" }} zoomControl={true} scrollWheelZoom={true}>
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                    {hasValidCoordinates && (
                      <Marker position={[lat, lng]} icon={markerIcon}>
                        <Popup><div className="p-1"><strong>{firstName} {lastName}</strong><p className="text-sm">{address}, {city}</p></div></Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </motion.div>

                {/* Expertise & Formation */}
                {hasExpertise && !isRppsOnly && (
                  <Section icon={<Stethoscope className="h-4 w-4" />} title="Expertise & Formation">
                    <div className="space-y-5">
                      {techniques.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Techniques</p>
                          <BadgeList items={techniques} color="teal" />
                          {other_techniques && <p className="text-sm text-gray-500 mt-1.5">{other_techniques}</p>}
                        </div>
                      )}
                      {uniqueBodyZones.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Zones du corps traitées</p>
                          <BadgeList items={uniqueBodyZones} color="blue" />
                        </div>
                      )}
                      {equipment.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Équipements</p>
                          <BadgeList items={equipment} color="purple" />
                          {other_equipment && <p className="text-sm text-gray-500 mt-1.5">{other_equipment}</p>}
                        </div>
                      )}
                      {sports.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Sports</p>
                          <BadgeList items={sports} color="green" />
                          {other_sports && <p className="text-sm text-gray-500 mt-1.5">{other_sports}</p>}
                        </div>
                      )}
                      {exercise_types.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Types d'exercices</p>
                          <BadgeList items={exercise_types} color="green" />
                        </div>
                      )}
                      {training_courses && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Formations suivies</p>
                          <p className="text-sm text-gray-700 whitespace-pre-line">{training_courses}</p>
                        </div>
                      )}
                      {cv_url && (
                        <a href={cv_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#008788] hover:underline font-medium">
                          <Briefcase className="h-4 w-4" />Voir le CV<ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </Section>
                )}

                {/* Le Cabinet */}
                {hasCabinet && !isRppsOnly && (
                  <Section icon={<Building className="h-4 w-4" />} title="Le cabinet">
                    <div className="space-y-4">
                      {practice_locations.length > 0 && (
                        <InfoRow icon={<Home className="h-4 w-4" />} label="Lieux d'exercice">
                          <BadgeList items={practice_locations} />
                          {home_visit_areas && practice_locations.includes("Domicile") && <p className="text-xs text-gray-500 mt-1">{home_visit_areas}</p>}
                        </InfoRow>
                      )}
                      {practitioners_count && (
                        <InfoRow icon={<Users className="h-4 w-4" />} label="Praticiens au cabinet"><span>{practitioners_count}</span></InfoRow>
                      )}
                      {(parking_type || parking_types.length > 0) && (
                        <InfoRow icon={<Car className="h-4 w-4" />} label="Stationnement">
                          {parking_types.length > 0 ? <BadgeList items={parking_types} /> : <span>{parking_type}</span>}
                        </InfoRow>
                      )}
                      {accessibility.length > 0 && (
                        <InfoRow icon={<AccessibilityIcon className="h-4 w-4" />} label="Accessibilité">
                          <BadgeList items={accessibility.map(formatAccessibility)} />
                        </InfoRow>
                      )}
                      {languages.length > 0 && (
                        <InfoRow icon={<Languages className="h-4 w-4" />} label="Langues parlées">
                          <BadgeList items={languages.map(formatLanguage)} />
                          {other_languages && <p className="text-xs text-gray-500 mt-1">{other_languages}</p>}
                        </InfoRow>
                      )}
                      {coordinated_practice.length > 0 && (
                        <InfoRow icon={<Building className="h-4 w-4" />} label="Exercice coordonné">
                          <BadgeList items={coordinated_practice} color="teal" />
                          {coordinated_practice_details && <p className="text-xs text-gray-500 mt-1">{coordinated_practice_details}</p>}
                        </InfoRow>
                      )}
                      {(other_activities.length > 0 || other_activities_details) && (
                        <InfoRow icon={<Activity className="h-4 w-4" />} label="Autres activités">
                          {other_activities.length > 0 && <BadgeList items={other_activities} />}
                          {other_activities_details && <p className="text-xs text-gray-500 mt-1">{other_activities_details}</p>}
                        </InfoRow>
                      )}
                      {association_name && (
                        <InfoRow icon={<Tag className="h-4 w-4" />} label="Associations"><span>{association_name}</span></InfoRow>
                      )}
                      {publications && (
                        <InfoRow icon={<Tag className="h-4 w-4" />} label="Publications"><span className="whitespace-pre-line">{publications}</span></InfoRow>
                      )}
                    </div>
                  </Section>
                )}

                {/* Informations pratiques */}
                {hasPractical && !isRppsOnly && (
                  <Section icon={<UserCheck className="h-4 w-4" />} title="Informations pratiques">
                    <div className="space-y-4">
                      {new_patients_policy && (
                        <InfoRow icon={<UserCheck className="h-4 w-4" />} label="Nouveaux patients"><span>{new_patients_policy}</span></InfoRow>
                      )}
                      {patient_types.length > 0 && (
                        <InfoRow icon={<Users className="h-4 w-4" />} label="Type de patientèle"><BadgeList items={patient_types} /></InfoRow>
                      )}
                      {session_duration && (
                        <InfoRow icon={<Timer className="h-4 w-4" />} label="Durée des séances"><span>{session_duration}</span></InfoRow>
                      )}
                      {waiting_list !== undefined && (
                        <InfoRow icon={<Clock className="h-4 w-4" />} label="Liste d'attente">
                          <span>{waiting_list ? `Oui${waiting_list_delay ? ` (${waiting_list_delay})` : ''}` : 'Non'}</span>
                        </InfoRow>
                      )}
                      {(practice_mode || practice_modes.length > 0) && (
                        <InfoRow icon={<Building className="h-4 w-4" />} label="Mode d'exercice">
                          <>
                            {practice_modes.length > 0 ? <BadgeList items={practice_modes} /> : <span>{practice_mode}</span>}
                            {honorary_details && <p className="text-xs text-gray-500 mt-1">{honorary_details}</p>}
                            {unconventional_practice_description && (
                              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                                <p className="text-xs font-medium text-amber-800">Pratique déconventionnée</p>
                                <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-line">{unconventional_practice_description}</p>
                              </div>
                            )}
                          </>
                        </InfoRow>
                      )}
                      {(payment_methods.length > 0 || payment_type) && (
                        <InfoRow icon={<CreditCard className="h-4 w-4" />} label="Modes de paiement">
                          {payment_methods.length > 0 ? <BadgeList items={payment_methods} /> : <span>{payment_type}</span>}
                        </InfoRow>
                      )}
                    </div>
                  </Section>
                )}

                {/* RPPS / ADELI */}
                {(rpps_number || adeli_number) && !isRppsOnly && (
                  <div className="flex gap-4 text-xs text-gray-400">
                    {rpps_number && <span>RPPS : {rpps_number}</span>}
                    {adeli_number && <span>ADELI : {adeli_number}</span>}
                  </div>
                )}
              </div>

              {/* ── RIGHT COLUMN (1/3) — Sidebar ── */}
              <div className="space-y-6">

                {/* Contact & RDV */}
                {hasContact && !isRppsOnly && (
                  <Section icon={<MessageSquare className="h-4 w-4" />} title="Contact & Rendez-vous" className="lg:sticky lg:top-24">
                    <div className="space-y-4">
                      {phone && (
                        <a href={`tel:${phone}`} className="flex items-center gap-3 p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors group">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-600 text-white group-hover:scale-105 transition-transform"><Phone className="h-4 w-4" /></div>
                          <div><p className="text-xs text-green-700 font-medium">Téléphone</p><p className="text-sm font-semibold text-green-900">{phone}</p></div>
                        </a>
                      )}
                      {professional_email && (
                        <a href={`mailto:${professional_email}`} className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors group">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white group-hover:scale-105 transition-transform"><Mail className="h-4 w-4" /></div>
                          <div className="min-w-0"><p className="text-xs text-blue-700 font-medium">Email</p><p className="text-sm font-semibold text-blue-900 truncate">{professional_email}</p></div>
                        </a>
                      )}
                      {professional_website && (
                        <a href={professional_website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-600 text-white"><Globe className="h-4 w-4" /></div>
                          <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Site web</p><p className="text-sm font-semibold text-gray-900 truncate">{professional_website.replace(/^https?:\/\//, '').replace(/^www\./, '')}</p></div>
                        </a>
                      )}
                      {(online_booking_link || booking_link) && (
                        <a href={online_booking_link || booking_link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full p-3 rounded-xl bg-[#008788] hover:bg-[#015963] text-white font-semibold text-sm transition-colors shadow-sm">
                          <Calendar className="h-4 w-4" />
                          Réserver en ligne
                        </a>
                      )}
                      {preferred_contact && (
                        <InfoRow icon={<MessageSquare className="h-4 w-4" />} label="Contact préférentiel">
                          <span>{preferred_contact}{preferred_contact_details && ` — ${preferred_contact_details}`}</span>
                        </InfoRow>
                      )}
                      {(appointment_methods.length > 0 || appointment_mode) && (
                        <InfoRow icon={<Calendar className="h-4 w-4" />} label="Prise de rendez-vous">
                          {appointment_methods.length > 0 ? <BadgeList items={appointment_methods} /> : <span>{appointment_mode}</span>}
                        </InfoRow>
                      )}
                      {secretariat_hours && (
                        <InfoRow icon={<Clock className="h-4 w-4" />} label="Horaires secrétariat">
                          <span className="whitespace-pre-line">{secretariat_hours}</span>
                        </InfoRow>
                      )}
                    </div>
                  </Section>
                )}
              </div>
            </div>
          </div>

        </main>
      </div>
    </>
  );
}
